import type { CancelBookingFormValues } from '@/scopes/account/booking-details/pages/components/CancelBookingAction/types'
import type { PartialCancellationFeeItem, PartialCancellationFeeItems } from '@/scopes/account/booking-details/pages/selectors'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useIntl } from '@/intl'
import { useBookingDetailsStore } from '@/scopes/account/booking-details/infrastructure/useBookingDetailsStore'
import {
  getIsCancelBookingMainBookerSelected,
  getPartialCancellationFeeItems,
} from '@/scopes/account/booking-details/pages/selectors'
import { CheckboxInput } from '@/scopes/common/components/FormElements/CheckboxInput'
import { EMAIL_INPUT_PATTERN } from '@/scopes/common/components/FormElements/patterns'
import { TextAreaInput } from '@/scopes/common/components/FormElements/TextAreaInput'
import { TextInput } from '@/scopes/common/components/FormElements/TextInput'
import styles from './CalculateCancellationFeeStep.module.css'

const NEW_MAIN_BOOKER_NAME_PATTERN = String.raw`^[a-zA-ZÀ-ÿĀ-žŁłŃńŇňŌōŐőŔŕŘřŚśŞşŠšŢţŤťŮůŰűŸÿŹźŻżŽž\s\-\.']{2,100}$`

function renderParticipantFee(
  item: PartialCancellationFeeItem,
  feeItems: PartialCancellationFeeItems,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
  formatCurrency: ReturnType<typeof useIntl>['formatCurrency'],
) {
  if (feeItems.status === 'loading' || feeItems.status === 'idle') {
    return (
      <span className={styles.priceLoader} aria-hidden data-testid="partial-cancellation-loader">
        <IndeterminateProgressCircle />
      </span>
    )
  }

  if (feeItems.status === 'error') {
    return null
  }

  if (!feeItems.isFree && (!item.isComplete || item.cancellationCost === null)) {
    return (
      <Typography variant="body-m-bold-snug" as="span">
        {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_unknown_cost' })}
      </Typography>
    )
  }

  if (feeItems.isFree) {
    return (
      <Typography variant="body-m-bold-snug" as="span">
        {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_free_cost' })}
      </Typography>
    )
  }

  return (
    <Typography variant="body-m-bold-snug" as="span">
      {formatCurrency(item.cancellationCost!)}
    </Typography>
  )
}

export function CalculateCancellationFeeStep() {
  const { formatMessage, formatCurrency } = useIntl()
  const { control } = useFormContext<CancelBookingFormValues>()
  const watchedParticipantIds = useWatch({ control, name: 'participantIds' })
  const participantIds = useMemo(() => watchedParticipantIds ?? [], [watchedParticipantIds])
  const isMainBookerCancelled = useBookingDetailsStore(
    state => getIsCancelBookingMainBookerSelected(state, participantIds),
  )
  const feeItems = useBookingDetailsStore(
    state => getPartialCancellationFeeItems(
      state,
      participantIds,
      titleKey => formatMessage({ id: titleKey }),
    ),
  )

  return (
    <>
      <div className={styles.infoParagraph}>
        <Typography variant="body-m-bold-snug" as="p">
          {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_cancellation_costs_heading' })}
        </Typography>
        <Typography variant="body-m-regular-snug" as="p">
          {formatMessage({
            id: 'booking_details_cancel_booking_partial_cancellation_fee_cancellation_costs_text',
          })}
        </Typography>
      </div>
      <Typography variant="body-m-regular-snug" as="p">
        {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_cancellation_costs_today_text' })}
      </Typography>

      {feeItems.status === 'error' && (
        <Banner color="warning" size="M" hasIcon>
          <Banner.Text>
            {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_unknown_cost' })}
          </Banner.Text>
        </Banner>
      )}

      {feeItems.items.map(item => (
        <ListItem key={item.participantId} variant="outlined">
          <div className={styles.priceSummary}>
            <div className={styles.participantLabel}>
              <Typography variant="body-m-regular-snug" as="span">
                {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_cancellation_costs_for_label' })}
              </Typography>
              <Typography variant="body-m-bold-snug" as="span">
                {item.name}
              </Typography>
            </div>
            <div className={styles.priceValue}>
              {renderParticipantFee(item, feeItems, formatMessage, formatCurrency)}
            </div>
          </div>
        </ListItem>
      ))}

      <div className={styles.infoParagraph}>
        <Typography variant="body-m-bold-snug" as="p">
          {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_additional_costs_heading' })}
        </Typography>
        <Typography variant="body-m-regular-snug" as="p">
          {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_additional_costs_text' })}
        </Typography>
        <Typography variant="body-m-regular-snug" as="p">
          {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_additional_costs_note_text' })}
        </Typography>
      </div>

      <Typography variant="body-m-regular-snug" as="p">
        {formatMessage({
          id: 'booking_details_cancel_booking_partial_cancellation_fee_room_layout_preference_label',
        })}
      </Typography>
      <TextAreaInput
        name="roomLayoutPreference"
        label={formatMessage({
          id: 'booking_details_cancel_booking_partial_cancellation_fee_room_layout_preference_field_label',
        })}
        placeholder={formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_room_layout_preference_placeholder' })}
      />

      {isMainBookerCancelled && (
        <div className={styles.newMainBooker}>
          <div className={styles.infoParagraph}>
            <Typography variant="body-m-bold-snug" as="p">
              {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_new_main_booker_heading' })}
            </Typography>
            <Typography variant="body-m-regular-snug" as="p">
              {formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_new_main_booker_text' })}
            </Typography>
          </div>
          <TextInput
            name="newMainBooker.name"
            label={formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_new_main_booker_name_label' })}
            required
            pattern={NEW_MAIN_BOOKER_NAME_PATTERN}
            requiredErrorMessage={formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_new_main_booker_name_required_error' })}
            patternErrorMessage={formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_new_main_booker_name_pattern_error' })}
          />
          <TextInput
            name="newMainBooker.email"
            label={formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_new_main_booker_email_label' })}
            required
            pattern={EMAIL_INPUT_PATTERN}
            requiredErrorMessage={formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_new_main_booker_email_required_error' })}
            patternErrorMessage={formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_new_main_booker_email_pattern_error' })}
          />
        </div>
      )}

      <CheckboxInput
        name="confirmPartialCancellationTerms"
        label={formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_confirm_terms_label' })}
        validationMessage={formatMessage({
          id: 'booking_details_cancel_booking_partial_cancellation_fee_confirm_terms_required_error',
        })}
      />

      <CheckboxInput
        name="requestPriceQuote"
        label={formatMessage({ id: 'booking_details_cancel_booking_partial_cancellation_fee_request_price_quote_label' })}
      />
    </>
  )
}
